import { SwarmFeed } from '../../components/overview';
import { PortfolioPerformanceChart } from '../../components/charts';
import { BlockInfoIcon, SegmentedControl, YearRangeSlider } from '../../components/ui';
import type { PeriodMode } from '../../functions/calculations';
import {
  getOverviewBlockSubtitle,
  getOverviewBlockTitle,
} from '../../content';
import { useOverviewSwarmFeed } from './useOverviewSwarmFeed';
import {
  OVERVIEW_CHART_TYPES,
  OVERVIEW_VIEW_BY_OPTIONS,
  type OverviewChartType,
  type OverviewViewBy,
} from './overview.config';
import type { ChartYearRange } from './useOverviewTrendChart';
import type { ChartSeries } from '../../types/charts';

interface OverviewTrendChartProps {
  selectedChart: OverviewChartType;
  viewBy: OverviewViewBy;
  yearRange: ChartYearRange | null;
  availableYears: string[];
  series: ChartSeries[];
  loading: boolean;
  unitCurrency: string;
  periodMode: PeriodMode;
  dateAnchorKey: string | null;
  onViewByChange: (id: OverviewViewBy) => void;
  onYearRangeChange: (range: ChartYearRange) => void;
  onChartTypeChange: (id: OverviewChartType) => void;
}

export function OverviewTrendChart({
  selectedChart,
  viewBy,
  yearRange,
  availableYears,
  series,
  loading,
  unitCurrency,
  periodMode,
  dateAnchorKey,
  onViewByChange,
  onYearRangeChange,
  onChartTypeChange,
}: OverviewTrendChartProps) {
  const swarm = useOverviewSwarmFeed({
    chart: selectedChart,
    periodMode,
    anchorDateKey: dateAnchorKey,
    yearRange: periodMode === 'all' && viewBy === 'month' ? yearRange : null,
  });

  return (
    <section className="overview-performance-card ui-chart-card">
      <div className="overview-performance-card__layout">
        <header className="overview-performance-card__chart-header">
          <div className="overview-performance-card__heading">
            <div className="overview-panel-card__title-row">
              <h2 className="overview-panel-card__title">
                {getOverviewBlockTitle('portfolioPerformance', 'Portfolio Performance')}
              </h2>
              <BlockInfoIcon blockId={selectedChart} />
            </div>
            <p className="overview-panel-card__subtitle">
              {getOverviewBlockSubtitle(
                'portfolioPerformance',
                'Historical view and swarm feed',
              )}
            </p>
          </div>
          <div className="overview-performance-card__period-controls">
            {periodMode === 'all' && viewBy === 'month' && (
              <YearRangeSlider
                className="overview-performance-card__range"
                years={availableYears}
                start={yearRange?.start ?? availableYears[0] ?? ''}
                end={yearRange?.end ?? availableYears[availableYears.length - 1] ?? ''}
                disabled={loading || availableYears.length === 0}
                onChange={onYearRangeChange}
              />
            )}
            <SegmentedControl
              className="overview-performance-card__period-toggle"
              ariaLabel="Chart period granularity"
              options={[...OVERVIEW_VIEW_BY_OPTIONS]}
              value={viewBy}
              onChange={onViewByChange}
            />
          </div>
        </header>

        <div className="overview-performance-card__insights-header">
          <SegmentedControl
            className="overview-performance-card__tabs"
            ariaLabel="Portfolio performance chart"
            options={[...OVERVIEW_CHART_TYPES]}
            value={selectedChart}
            onChange={onChartTypeChange}
          />
        </div>

        <div className="overview-performance-card__chart">
          <PortfolioPerformanceChart
            className="overview-trend-chart"
            series={series}
            currencyCode={unitCurrency}
            loading={loading}
          />
        </div>

        <SwarmFeed
          records={swarm.records}
          loading={swarm.loading}
          error={swarm.error}
          locationKey={swarm.locationKey}
          operatingModelKey={swarm.operatingModelKey}
          locationOptions={swarm.locationOptions}
          operatingModelOptions={swarm.operatingModelOptions}
          onLocationChange={swarm.setLocationKey}
          onOperatingModelChange={swarm.setOperatingModelKey}
          hasMore={swarm.hasMore}
          loadingMore={swarm.loadingMore}
          onLoadMore={swarm.loadMore}
        />
      </div>
    </section>
  );
}
